import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Gear, EnvelopeSimple, Trash, Plus, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdminLike = ['admin', 'owner', 'assistant', 'accountant'].includes(user?.role);
  const isOwner = ['admin', 'owner'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [effective, setEffective] = useState({ emails: [], source: '' });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { api } = await import('../lib/api');
      const [cfg, eff] = await Promise.all([
        api.get('/settings/report-recipients'),
        api.get('/settings/report-recipients/effective'),
      ]);
      setEmails(cfg.data?.emails || []);
      setUpdatedAt(cfg.data?.updated_at || null);
      setUpdatedBy(cfg.data?.updated_by_name || null);
      setEffective({
        emails: eff.data?.emails || [],
        source: eff.data?.source || '',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdminLike) load(); }, [isAdminLike, load]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Invalid email format');
      return;
    }
    setAdding(true);
    try {
      const { api } = await import('../lib/api');
      const res = await api.post('/settings/report-recipients', { email });
      setEmails(res.data?.emails || []);
      setNewEmail('');
      toast.success(`Added: ${email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from report recipients?`)) return;
    try {
      const { api } = await import('../lib/api');
      const res = await api.delete(`/settings/report-recipients/${encodeURIComponent(email)}`);
      setEmails(res.data?.emails || []);
      toast.success(`Removed: ${email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove');
    }
  };

  if (!isAdminLike) {
    return (
      <div className="p-6" data-testid="settings-access-denied">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          Access denied — Admin / Owner / CA / Assistant only.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-2">
        <Gear size={24} weight="duotone" className="text-[#002FA7]" />
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      </div>

      {/* Report Recipients Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="report-recipients-section">
        <div className="flex items-center gap-2 mb-1">
          <EnvelopeSimple size={20} weight="duotone" className="text-[#002FA7]" />
          <h2 className="text-lg font-semibold text-gray-900">Report Recipients</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Email list for the <b>Monthly Recovery Report</b> (auto-sent on 1st of every month).
          Leave empty to auto-use all Owner + Admin + CA users.
        </p>

        {/* Info banner — effective list */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4 flex items-start gap-2" data-testid="effective-recipients-banner">
          <Info size={18} weight="duotone" className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <div className="font-semibold mb-0.5">
              Next send will email {effective.emails.length} recipient{effective.emails.length !== 1 ? 's' : ''}
              {effective.source === 'custom' ? ' (using custom list below)' : ' (auto-discovered — no custom list set)'}
            </div>
            <div className="text-blue-800 break-all">
              {effective.emails.length > 0 ? effective.emails.join(', ') : '— none found'}
            </div>
          </div>
        </div>

        {/* Add new email — Owner only */}
        {isOwner && (
          <div className="flex gap-2 mb-4" data-testid="add-recipient-form">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              placeholder="finance@company.com"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
              data-testid="new-recipient-email-input"
              disabled={adding}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={adding || !newEmail.trim()}
              className="inline-flex items-center gap-1 px-4 py-2 bg-[#002FA7] hover:bg-[#00238a] disabled:bg-gray-400 text-white rounded-md text-sm font-semibold"
              data-testid="add-recipient-btn"
            >
              <Plus size={14} weight="bold" />
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}

        {/* Custom list */}
        <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-testid="recipients-list">
          {loading && (
            <div className="p-4 text-sm text-gray-500">Loading…</div>
          )}
          {!loading && emails.length === 0 && (
            <div className="p-4 text-sm text-gray-500 italic" data-testid="recipients-empty">
              Koi custom recipient nahi — system Owner/Admin/CA users ko auto-discover kar raha hai. 
              {isOwner && ' Custom list chahiye toh upar email add karo.'}
            </div>
          )}
          {!loading && emails.map((email) => (
            <div key={email} className="flex items-center justify-between px-4 py-2.5" data-testid={`recipient-row-${email}`}>
              <span className="text-sm text-gray-900 font-mono break-all">{email}</span>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => handleRemove(email)}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded px-2 py-1"
                  data-testid={`remove-recipient-${email}`}
                  aria-label={`Remove ${email}`}
                >
                  <Trash size={14} />
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Meta footer */}
        {updatedAt && (
          <div className="mt-3 text-xs text-gray-500">
            Last updated: {new Date(updatedAt).toLocaleString()} {updatedBy && <>by <b>{updatedBy}</b></>}
          </div>
        )}
        {!isOwner && isAdminLike && (
          <div className="mt-3 text-xs text-gray-500 italic">
            Read-only view — only Owner can modify recipients.
          </div>
        )}
      </div>
    </div>
  );
}
